<template>
    <modal
        :show="show"
        body-classes="p-0"
        modal-classes="modal-sm"
        @update:show="$emit('update:show', $event)"
    >
        <card class="mb-0 card-form">
            <template slot="header">
                <h2 class="modal-title">Add Pre-Order</h2>
                <button
                    type="button"
                    class="close"
                    aria-label="Close"
                    @click="$emit('update:show', false)"
                >
                    <i class="tim-icons icon-simple-remove" />
                </button>
            </template>
            <ValidationObserver
                ref="form"
                v-slot="{ invalid, validate, pristine }"
            >
                <form
                    autocomplete="off"
                    @submit.prevent="validate().then(addPreOrder)"
                >
                    <div class="row">
                        <div class="col-12">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Job/Project Name"
                                rules="required"
                            >
                                <div class="form-group">
                                    <base-input
                                        v-model="preOrder.CustomerJobID"
                                        name="jobName"
                                        autocomplete="off"
                                        type="text"
                                        label="Job/Project Name"
                                        placeholder=""
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <base-input
                                name="expectedAt"
                                type="date"
                                label="Pre-Order Expected By"
                                :error="errors[0]"
                            >
                                <el-date-picker
                                    v-model="preOrder.expectedAt"
                                    :picker-options="{
                                        disabledDate: isPreOrderDateInvalid,
                                    }"
                                    format="MM/dd/yyyy"
                                    type="date"
                                    placeholder="Select date"
                                />
                            </base-input>
                        </div>
                    </div>
                    <div v-if="showClientSelect" class="row">
                        <div class="col-12">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Client Type"
                                :rules="showClientSelect ? 'required' : ''"
                            >
                                <label>Client Type</label>
                                <div class="form-group">
                                    <el-radio-group v-model="clientType">
                                        <el-radio-button label="Customer" />
                                        <el-radio-button label="Designer" />
                                    </el-radio-group>
                                    <label class="error">{{ errors[0] }}</label>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div v-if="showClientSelect" class="row">
                        <div v-if="clientType === 'Customer'" class="col-md-12">
                            <label>Customer</label>
                            <div class="form-group">
                                <v-select
                                    v-model="preOrder.customerID"
                                    :reduce="(customer) => customer.id"
                                    :options="customers"
                                    placeholder="Select customer"
                                    label="name"
                                    @search="searchCustomers(...arguments)"
                                >
                                    <template slot="no-options">
                                        <span v-if="searchCustomersText">
                                            No results found.
                                        </span>
                                        <span v-else>Type to search...</span>
                                    </template>
                                    <template slot="option" slot-scope="option">
                                        {{ option.name }}
                                    </template>
                                </v-select>
                            </div>
                        </div>
                        <div v-if="clientType === 'Designer'" class="col-md-12">
                            <label>Designer</label>
                            <div class="form-group">
                                <v-select
                                    v-model="preOrder.designerID"
                                    :reduce="(designer) => designer.id"
                                    :options="designers"
                                    label="fullName"
                                    placeholder="Select designer"
                                    @search="searchDesigners(...arguments)"
                                >
                                    <template slot="no-options">
                                        <span v-if="searchDesignersText">
                                            No results found.
                                        </span>
                                        <span v-else>Type to search...</span>
                                    </template>
                                    <template slot="option" slot-scope="option">
                                        <div>
                                            <b>{{ option.fullName }}</b>
                                        </div>
                                        <div>
                                            {{
                                                option.customer
                                                    ? option.customer.name
                                                    : ""
                                            }}
                                        </div>
                                    </template>
                                </v-select>
                            </div>
                        </div>
                    </div>
                    <base-button
                        id="addPreOrder"
                        :disabled="preOrderSubmitted || invalid || pristine"
                        native-type="submit"
                        type="primary"
                        class="btn-fill"
                    >
                        Add Pre-Order
                    </base-button>
                </form>
            </ValidationObserver>
        </card>
    </modal>
</template>
<script>
import { Modal } from "src/components/UI";
import vSelect from "vue-select";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { DatePicker, RadioGroup, RadioButton } from "element-ui";

export default {
    name: "NewPreOrder",
    components: {
        vSelect,
        Modal,
        [DatePicker.name]: DatePicker,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        ValidationObserver,
        ValidationProvider,
    },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        showClientSelect: {
            type: Boolean,
            required: true,
        },
        customers: {
            type: Array,
            default: () => [],
        },
        designers: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            preOrder: {},
            clientType: null,
            preOrderSubmitted: false,
            searchDesignersText: null,
            searchCustomersText: null,
        };
    },
    watch: {
        clientType(type) {
            if (type === "Customer") {
                delete this.preOrder.designerID;
            } else if (type === "Designer") {
                delete this.preOrder.customerID;
            }
        },
    },
    methods: {
        isPreOrderDateInvalid(date) {
            const today = new Date(new Date().setHours(0, 0, 0, 0));
            return today > date;
        },
        addPreOrder() {
            // remove timezone info
            this.preOrder.expectedAt = this.preOrder.expectedAt.toDateString();
            // need to set the customer id to that of the designer's associated customer
            if (this.showClientSelect && this.preOrder.designerID) {
                const designer = this.designers.find(
                    (d) => d.id === this.preOrder.designerID,
                );
                this.preOrder.customerID = designer?.customer?.id;
            }
            this.$emit("addPreOrder", this.preOrder);
            this.preOrderSubmitted = true;
        },
        searchDesigners(search, loading) {
            this.searchDesignersText = search;
            this.$emit("searchDesigners", { search, loading });
        },
        searchCustomers(search, loading) {
            this.searchCustomersText = search;
            this.$emit("searchCustomers", { search, loading });
        },
    },
};
</script>
